import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Briefcase,
  BarChart3,
  Users,
  Link,
  Calculator,
  FolderOpen,
  LogOut,
  Menu,
  X,
  ChevronDown,
  UserCheck,
  Activity,
  Settings,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { BusinessSelector, useBusiness } from '@/contexts/BusinessContext';
import { Logo, LogoText } from '@/components/Logo';
import { WalkthroughProvider, useWalkthroughContext, HelpButton } from '@/walkthrough';
import { NotificationBell } from '@/components/NotificationBell';

// ===========================================
// Types
// ===========================================

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
}

// ===========================================
// Navigation Items
// ===========================================

const repNavItems: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
  { to: '/companies', label: 'Companies', icon: <Building2 size={20} /> },
  { to: '/projects', label: 'My Projects', icon: <Briefcase size={20} /> },
  { to: '/reports', label: 'Reports', icon: <BarChart3 size={20} /> },
];

const adminNavItems: NavItem[] = [
  { to: '/admin', label: 'Admin Dashboard', icon: <LayoutDashboard size={20} />, adminOnly: true },
  { to: '/admin/projects', label: 'All Projects', icon: <Briefcase size={20} />, adminOnly: true },
  { to: '/admin/users', label: 'Users & Reps', icon: <Users size={20} />, adminOnly: true },
  { to: '/admin/partners', label: 'Partner Access', icon: <UserCheck size={20} />, adminOnly: true },
  { to: '/admin/rules', label: 'Commission Rules', icon: <Calculator size={20} />, adminOnly: true },
  { to: '/admin/reports', label: 'All Reports', icon: <BarChart3 size={20} />, adminOnly: true },
  { to: '/admin/evidence', label: 'Evidence Library', icon: <FolderOpen size={20} />, adminOnly: true },
  { to: '/admin/settings', label: 'Settings', icon: <Settings size={20} />, adminOnly: true },
  { to: '/admin/diagnostics', label: 'Diagnostics', icon: <Activity size={20} />, adminOnly: true },
];

// ===========================================
// Main Layout Component
// ===========================================

export function Layout() {
  return (
    <WalkthroughProvider>
      <LayoutInner />
    </WalkthroughProvider>
  );
}

function LayoutInner() {
  const { user, signOut, canAccessAdmin } = useAuth();
  const { currentBusiness, getBusinessLabel } = useBusiness();
  const { walkthroughEnabled, replayTour } = useWalkthroughContext();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        data-tour="crm-sidebar"
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-200
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo / Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b">
          <Logo className="h-8" />
          <button
            className="lg:hidden p-2 rounded-md hover:bg-gray-100"
            onClick={closeSidebar}
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {/* Rep Navigation */}
          <div className="mb-4">
            <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Main
            </p>
            <div className="mt-2 space-y-1">
              {repNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={closeSidebar}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors
                    ${
                      isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`
                  }
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Admin Navigation */}
          {canAccessAdmin && (
            <div>
              <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Admin
              </p>
              <div className="mt-2 space-y-1">
                {adminNavItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors
                      ${
                        isActive
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`
                    }
                  >
                    {item.icon}
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          )}
        </nav>

        {/* User info at bottom */}
        <div className="border-t p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
              {user?.displayName?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">
                {user?.displayName}
              </p>
              <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64 flex flex-col min-h-screen">
        {/* Top header */}
        <header className="sticky top-0 z-30 flex items-center justify-between h-16 px-4 bg-white border-b shadow-sm">
          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-md hover:bg-gray-100"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={24} />
          </button>

          {/* Business selector + Help button */}
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-sm text-gray-600">
              View:
            </span>
            <BusinessSelector />
            {walkthroughEnabled && replayTour && (
              <HelpButton onClick={replayTour} />
            )}
          </div>

          {/* Notification bell */}
          <NotificationBell />

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-100"
            >
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                {user?.displayName?.charAt(0) || 'U'}
              </div>
              <ChevronDown size={16} className="text-gray-500" />
            </button>

            {/* Dropdown menu */}
            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border z-50">
                  <div className="px-4 py-3 border-b">
                    <p className="text-sm font-medium text-gray-800">
                      {user?.displayName}
                    </p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="py-4 px-6 border-t bg-white text-center text-xs text-gray-500">
          <span className="font-medium">{getBusinessLabel(currentBusiness)}</span>{' '}
          <LogoText className="text-xs" /> &copy; {new Date().getFullYear()}
        </footer>
      </div>
    </div>
  );
}
